/** @format */

import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import useCourseClassrooms from "./useCourseClassrooms";

const useCourseClassroomDetails = (courseId) => {
  const [classroomDetails, setClassroomDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get the basic course classroom data
  const { classrooms: courseClassrooms, loading: courseClassroomsLoading } = useCourseClassrooms(courseId);

  // Fetch full classroom details for each course classroom
  useEffect(() => {
    const fetchClassroomDetails = async () => {
      if (!courseClassrooms.length) {
        setClassroomDetails([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Raw course classrooms data:", courseClassrooms);
        console.log("First classroom structure:", courseClassrooms[0]);
        
        const detailsPromises = courseClassrooms.map(async (courseClassroom) => {
          try {
            console.log("Processing course classroom:", courseClassroom);
            
            // Extract classroomId - handle both string and object formats
            let classroomId = courseClassroom.classroomId;
            console.log("Original classroomId:", classroomId, typeof classroomId);
            
            if (typeof classroomId === 'object' && classroomId !== null) {
              // If it's an object, try to extract the ID from various possible properties
              classroomId = classroomId.id || classroomId.uid || classroomId.classroomId || classroomId._id;
              
              // If it's still an object, try to stringify it or get its string representation
              if (typeof classroomId === 'object' && classroomId !== null) {
                console.log("classroomId is still an object after extraction:", classroomId);
                // Try to get a string representation
                classroomId = classroomId.toString ? classroomId.toString() : JSON.stringify(classroomId);
                console.log("Stringified classroomId:", classroomId);
              }
            }
            
            console.log("Final classroomId:", classroomId, typeof classroomId);
            
            // Check if classroomId is a valid Firestore document ID (basic validation)
            const isValidDocId = classroomId && 
              typeof classroomId === 'string' && 
              classroomId !== 'null' && 
              classroomId !== 'undefined' &&
              classroomId.length > 0 &&
              !classroomId.includes(' ') &&
              !classroomId.includes('[') &&
              !classroomId.includes(']');
            
            console.log("Is valid doc ID:", isValidDocId);
            
            // If we have a valid string classroomId, fetch the full classroom details
            if (isValidDocId) {
              try {
                const classroomRef = doc(db, "classrooms", classroomId);
                const classroomDoc = await getDoc(classroomRef);
              
                if (classroomDoc.exists()) {
                  const classroomData = classroomDoc.data();
                  return {
                    ...courseClassroom,
                    // Use the full classroom data for display
                    name: classroomData.name || courseClassroom.classroomName || "Unknown Classroom",
                    subject: classroomData.subject || courseClassroom.classroomSubject || "",
                    category: classroomData.category || courseClassroom.classroomCategory || "",
                    description: classroomData.description || "",
                    status: classroomData.status || courseClassroom.status || "active",
                    memberCount: classroomData.memberCount || 0,
                    maxMembers: classroomData.maxMembers || 0,
                    profileImageUrl: classroomData.profileImageUrl || "",
                    // Keep the course-classroom specific data
                    addedAt: courseClassroom.addedAt,
                    addedBy: courseClassroom.addedBy,
                    addedByRole: courseClassroom.addedByRole,
                  };
                } else {
                  // Fallback to stored data if classroom doesn't exist
                  return {
                    ...courseClassroom,
                    name: courseClassroom.classroomName || "Unknown Classroom",
                    subject: courseClassroom.classroomSubject || "",
                    category: courseClassroom.classroomCategory || "",
                    description: "",
                    status: courseClassroom.status || "active",
                    memberCount: 0,
                    maxMembers: 0,
                    profileImageUrl: "",
                  };
                }
              } catch (docError) {
                console.error("Error creating doc reference with classroomId:", classroomId, docError);
                // Fallback to stored data if doc creation fails
                return {
                  ...courseClassroom,
                  name: courseClassroom.classroomName || "Unknown Classroom",
                  subject: courseClassroom.classroomSubject || "",
                  category: courseClassroom.classroomCategory || "",
                  description: "",
                  status: courseClassroom.status || "active",
                  memberCount: 0,
                  maxMembers: 0,
                  profileImageUrl: "",
                };
              }
            } else {
              // Fallback to stored data if no classroomId
              return {
                ...courseClassroom,
                name: courseClassroom.classroomName || "Unknown Classroom",
                subject: courseClassroom.classroomSubject || "",
                category: courseClassroom.classroomCategory || "",
                description: "",
                status: courseClassroom.status || "active",
                memberCount: 0,
                maxMembers: 0,
                profileImageUrl: "",
              };
            }
          } catch (error) {
            console.error(`Error fetching classroom details for classroomId:`, courseClassroom.classroomId, error);
            // Return fallback data
            return {
              ...courseClassroom,
              name: courseClassroom.classroomName || "Unknown Classroom",
              subject: courseClassroom.classroomSubject || "",
              category: courseClassroom.classroomCategory || "",
              description: "",
              status: courseClassroom.status || "active",
              memberCount: 0,
              maxMembers: 0,
              profileImageUrl: "",
            };
          }
        });

        const details = await Promise.all(detailsPromises);
        setClassroomDetails(details);
        console.log("Course classroom details:", details);
      } catch (err) {
        console.error("Error fetching classroom details:", err);
        setError("Failed to fetch classroom details");
      } finally {
        setLoading(false);
      }
    };

    fetchClassroomDetails();
  }, [courseClassrooms]);

  return {
    classroomDetails,
    loading: loading || courseClassroomsLoading,
    error,
  };
};

export default useCourseClassroomDetails;
